// frontend/src/utils/gridDefaults.js
// Universal AG-Grid defaults shared across all grids.
//
// === Column sizing ===
//
//   AG Grid sizes the columns, on every grid, and keeps them sized
//   (`autoSizeStrategy` with `continuous`, AG Grid 36.2 autoSize.d.ts
//   `ContinuousAutoSizeOptions`): on new rows, a new page, an edit, a column
//   shown or hidden and a change in the grid's width. A grid with no width (a
//   hidden tab) is sized when it gets one. Every grid takes AUTO_SIZE_TO_CONTENTS
//   from the props below; a grid that should span the page with no flex column
//   passes AUTO_SIZE_TO_FILL, which also stretches the measured columns over
//   leftover width (`scaleUpToFitGridWidth`). Every master-detail sub-grid is
//   one of those (detailGridDefaults), and its columns never flex: a flex
//   column gets only the width the measured columns leave, which in a narrow
//   sub-grid cuts off the product name. Nothing here or in a page measures
//   by hand — NEVER call autoSizeAllColumns or sizeColumnsToFit: any width write
//   drops a column's flex (AG Grid 36 `Column.setActualWidth`).
//
//   AG Grid leaves alone:
//     - flex columns (`flexColumn()`), which share the width the others leave;
//     - columns the merchant sized by hand — a header-edge drag or double-click,
//       or a saved width put back (utils/merchantColumnLayout) — until they pick
//       Reset columns in the header menu;
//     - `suppressAutoSize` columns (icon action columns, the selection column).
//
// === Column types ===
//
//   Alignment comes from the column `type` (hooks/useColumnTypes.js) and
//   nowhere else — never a hand-written `cellClass: 'ag-…-aligned-cell'` or a
//   `cellStyle` on the column (utils/gridConventions.ratchet.test.js). The types
//   carry their alignment as a cellClassRules entry rather than a cellClass
//   string, so a column that adds its own cellClass (a report's group tint)
//   keeps the type's alignment: AG Grid replaces a cellClass string but merges
//   cellClassRules. A column that needs both a number's formatting and a
//   centred chip lists both types, `type: ['numberColumn', 'centeredColumn']`
//   — the later type wins the alignment.
//
//   NOTE: Do NOT use AG-Grid built-in type names ('numericColumn', 'dateColumn')
//   as they right-align content and override custom definitions.
//
// === Column roles ===
//
//   Measured columns: no flags — sized to the widest rendered cell (or the
//     header). Under AUTO_SIZE_TO_FILL they may also be scaled up to fill
//     leftover width.
//   Flex columns: spread `flexColumn()` — the columns that fill the page. It
//     pairs `flex` with `suppressAutoSize`, so a double-click on the header edge
//     cannot turn the column into a fixed pixel width. Flex columns share the leftover width by ratio, so a
//     `flexColumn(2)` name next to `flexColumn()` codes reads as intended;
//     only centre columns flex — a pinned column keeps its declared width.
//     Each carries a width floor (FLEX_COLUMN_MIN_WIDTH, or the column's own
//     as the second argument), so on a narrow screen the grid scrolls
//     sideways rather than clipping every text column.
//     Only text flexes. A date, number or boolean column that flexes takes a
//     share of width it cannot use, and the text beside it is the column that
//     clips (utils/gridConventions.ratchet.test.js).
//   Chip / badge renderer columns: measured like any other column when the
//     number of chips varies per row — one fixed
//     width cannot fit both a one-chip row and a four-chip one. Pair it with a
//     minWidth floor and a maxWidth ceiling: past the ceiling the chips clip,
//     so the column also needs a tooltipValueGetter carrying the full list.
//     This measures the widest chip row currently RENDERED, so the column
//     re-fits on each page.
//     A single chip from a closed vocabulary (a status) renders through
//     components/grid/ChipSlot, which reserves the width of every chip the
//     column can show, and is measured — never a hand-picked width, which
//     clips the longer labels of another language.
//   Selection column (the checkbox AG Grid inserts): declared once, as
//     `selectionColumnDef` below, handed to every grid by defaultGridProps and
//     detailGridDefaults — never per page. AG Grid
//     sets `suppressAutoSize` on it already (main.esm.mjs `createColDef`), so
//     auto-sizing never touches the width declared there.
//   Action columns (buttons / icons): build with actionColumn() / iconActionColumn() below —
//     never hand-pick a width for a text button (it clips or over-pads across locales).
//     A single-icon column's cell is components/grid/GridIconAction.
//     One is locked to the grid's edge: last by default, first with
//     `lockPosition: 'left'` for a leading action. No column can be dragged
//     past it.

import LoadingOverlay from '../components/grid/LoadingOverlay';
import NoRowsOverlay from '../components/grid/NoRowsOverlay';
import SkeletonCell from '../components/grid/SkeletonCell';
import { pinnedTotalRowClass } from './pinnedTotals';
import { getColumnMenuItems } from './merchantColumnLayout';

// Single shared overlay selector used by all grids. Routes loading vs no-rows
// to the brand-aligned skeleton overlay vs the context-aware empty state.
// A page sets its no-rows copy with <GridEmptyState> (see NoRowsOverlay).
export const overlayComponentSelector = (params) => {
  if (params.overlayType === 'loading') {
    return { component: LoadingOverlay };
  }
  if (params.overlayType === 'noRows') {
    return { component: NoRowsOverlay };
  }
  return undefined;
};

// Column visibility panel — columns only, no grouping/pivot/filter panels.
export const COLUMN_PICKER_SIDEBAR = {
  toolPanels: [{
    id: 'columns',
    labelDefault: 'Columns',
    labelKey: 'columns',
    iconKey: 'columns',
    toolPanel: 'agColumnsToolPanel',
    toolPanelParams: {
      suppressRowGroups: true,
      suppressValues: true,
      suppressPivots: true,
      suppressPivotMode: true,
    },
  }],
};

// The two cell states every grid shows the same way (styles/pages.css). The
// convention has a third — `ag-cell-opens-dialog`, a read-only cell the
// merchant changes through a window — but AG Grid cannot be asked whether a
// cell opens one, so that state is the only one a column opts into by hand.
//
//   ag-cell-editable-hint  "You can type here." The cell is drawn as a small
//     input field with a text cursor; a read-only cell stays plain text. The
//     cell asks the grid the same question the editor does
//     (`column.isCellEditable`: the colDef's `editable`, the group-row rules,
//     an SoR lock) — never a second predicate that could disagree with it.
//     A checkbox cell (useColumnTypes `booleanColumn`) wears no box: the
//     checkbox is its own control, greyed where the cell is read-only.
//   ag-cell-unsaved  "You changed this and have not saved." An editable grid
//     hands its dirty-cell test to the grid ONCE, as `context.unsavedCellClass`
//     (useEditableGrid returns it ready-made as `gridContext`); a grid with no
//     such test has no unsaved cells.
//
// Both live on `defaultColDef` because AG Grid deep-merges that into every
// column (ag-grid-community.js `_addColumnDefaultAndTypes` -> `_mergeDeep`): a
// column's own `cellClassRules` add to these rather than replace them, so no
// grid and no column has to opt in — and none can forget. Pinned in
// gridDefaults.test.js; utils/gridConventions.ratchet.test.js keeps per-column
// copies from coming back.
export const cellStateClassRules = {
  'ag-cell-editable-hint': (params) => params.colDef.cellRenderer !== 'agCheckboxCellRenderer'
    && params.column.isCellEditable(params.node),
  'ag-cell-unsaved': (params) => {
    const unsavedCellClass = params.context?.unsavedCellClass;
    return unsavedCellClass ? unsavedCellClass(params) : false;
  },
};

// Every column can be dragged to a new place and to a new width; what the
// merchant lays out is saved per user (utils/merchantColumnLayout,
// hooks/useGridState). A grid whose column order means something keeps its
// columns in place (useGridState's `movableColumns`). Pinning is the
// developer's: a dragged column never crosses into a pinned section, because
// pinning is not saved.
//
// A header click goes ascending ⇄ descending, never back to unsorted: rows
// always come in some order (a server grid's own, see restoreInitialSort), so
// a header with no arrow would hide the order the merchant is looking at.
export const defaultColDef = {
  lockPinned: true,
  sortable: true,
  sortingOrder: ['asc', 'desc'],
  suppressHeaderMenuButton: true,
  floatingFilter: false,
  cellClassRules: cellStateClassRules,
};

// A column that fills the page: `flex` is its share of the leftover width,
// `suppressAutoSize` keeps a double-click on its header edge from turning that
// share into a pixel width. Spread it into the colDef.
// `minWidth` is the floor a flex column shrinks to when the grid is narrower
// than its columns (a phone): below it AG Grid scrolls the grid sideways
// instead of clipping the text to its first letter. A column with its own
// floor passes it here, never as a `minWidth` beside the spread.
export const FLEX_COLUMN_MIN_WIDTH = 140;
export const flexColumn = (flex = 1, minWidth = FLEX_COLUMN_MIN_WIDTH) => ({ flex, minWidth, suppressAutoSize: true });

// AG Grid's re-size waits while any rendered row is still loading: a server-side
// block shows skeleton cells first (SkeletonCell), and measuring those would
// shrink every column for a moment. The block's arrival triggers the re-size
// again (`modelUpdated`).
function rowsHaveLoaded({ api }) {
  for (let i = api.getFirstDisplayedRowIndex(); i <= api.getLastDisplayedRowIndex(); i++) {
    if (api.getDisplayedRowAtIndex(i).stub) return false;
  }
  return true;
}

// The two sizing strategies (see "Column sizing" in the header).
export const AUTO_SIZE_TO_CONTENTS = {
  type: 'fitCellContents',
  continuous: true,
  shouldAutoSizeColumns: rowsHaveLoaded,
};
export const AUTO_SIZE_TO_FILL = { ...AUTO_SIZE_TO_CONTENTS, scaleUpToFitGridWidth: true };

// The width of a column that holds ONE small control: the checkbox column and
// a single-icon action column share it, so a grid's leading columns line up.
// GridIconAction draws a single-icon row action as a small MUI IconButton
// around an ICON.md glyph: 26px. With the 6px `.ag-action-cell` gutter each side
// (pages.css) that rounds up to 40.
export const CONTROL_COLUMN_WIDTH = 40;

// A browser whose scrollbars float over content (Firefox on Windows 11)
// reports a 0px scrollbar. AG Grid then reserves no room for its own scrollbar
// strips yet still lays the vertical one over the last column, so every grid
// looks cut off. `scrollbarWidth` is AG Grid's option for a scrollbar size the
// browser does not report: the grid keeps that band free in its column math,
// exactly as it keeps a classic 8px bar's. 16px is the width AG Grid itself
// gives the strip of an unreported scrollbar (`adjustedScrollbarWidth` in its
// bundle).
//
// The probe is the one AG Grid runs (`initScrollbarWidthAndVisibility`): a
// box that always scrolls, measured as its border box minus its client box. A
// layout that cannot measure (jsdom) reports null and leaves AG Grid's default.
// Pinned against the bundle in styles/gridScrollbars.test.js.
export const OVERLAY_SCROLLBAR_WIDTH = 16;

export function measureScrollbarWidth() {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;width:100px;height:100px;overflow:scroll;opacity:0';
  document.body.appendChild(probe);
  const width = probe.offsetWidth - probe.clientWidth;
  const unmeasurable = width === 0 && probe.clientWidth === 0;
  probe.remove();
  return unmeasurable ? null : width;
}

const scrollbarProps = measureScrollbarWidth() === 0 ? { scrollbarWidth: OVERLAY_SCROLLBAR_WIDTH } : {};

// Shared by every grid the merchant can lay out (full-page and detail grids).
//   - `autoSizeStrategy`: AG Grid sizes the columns (see "Column sizing" in the
//     header); a fill-width grid sets AUTO_SIZE_TO_FILL over it.
//   - `maintainColumnOrder`: a page that rebuilds its columnDefs (a language
//     switch, the product grouping toggle) keeps the order the merchant
//     dragged; by default AG Grid re-applies the columnDefs order. A column
//     new to the grid goes beside its columnDefs neighbour
//     (utils/merchantColumnLayout), not last where AG Grid files it.
//   - `suppressDragLeaveHidesColumns`: a column dragged off the grid stays
//     visible — hiding is the column picker's job.
//   - `getColumnMenuItems`: the header's right-click menu holds Reset columns
//     only (utils/merchantColumnLayout).
const merchantLayoutProps = {
  autoSizeStrategy: AUTO_SIZE_TO_CONTENTS,
  maintainColumnOrder: true,
  suppressDragLeaveHidesColumns: true,
  getColumnMenuItems,
};

// The checkbox column of every grid with row selection, full-page and detail.
// AG Grid's own is 50px with the checkbox against its left edge, which leaves a
// dead gap before the first data column. Sized and centred like an icon action
// column instead (`ag-action-cell`; the checkbox's own margins are dropped in
// pages.css). AG Grid's own definition locks it first. Unused on grids
// without selection.
const selectionColumnDef = {
  width: CONTROL_COLUMN_WIDTH,
  minWidth: CONTROL_COLUMN_WIDTH,
  maxWidth: CONTROL_COLUMN_WIDTH,
  cellClass: 'ag-action-cell grid-selection-cell',
  headerClass: 'grid-selection-header',
};

// Props shared by every full-page grid and every grid in a popup (SSRM and
// client-side).
// Row height is NOT set here — it is a theme parameter (see useAgGridTheme). A
// `rowHeight` grid option would set the row DOM height without updating the
// theme's --ag-row-height, desyncing cell line-height and breaking vertical
// centring.
export const defaultGridProps = {
  ...scrollbarProps,
  ...merchantLayoutProps,
  selectionColumnDef,
  cellSelection: true,
  domLayout: 'normal',
  popupParent: document.body,
  sideBar: COLUMN_PICKER_SIDEBAR,
  suppressColumnVirtualisation: true,
  suppressAggFuncInHeader: true,
  overlayComponentSelector,
  // SSRM rows mid-fetch get a per-row shimmer; harmless for client-side grids.
  loadingCellRenderer: SkeletonCell,
  // Tags pinned-bottom totals rows so themeQuartz can style them distinctly.
  getRowClass: pinnedTotalRowClass,
  // SSRM cache hygiene — set as INITIAL props (JSX) because AG Grid rejects
  // updates via setGridOption for both (warning #22). Client-side grids
  // ignore them.
  //   - `maxBlocksInCache`: bounds the per-grid block memory; by default AG
  //     Grid keeps every requested block for the grid's lifetime.
  //   - `blockLoadDebounceMillis`: keeps fast scrolling from firing one fetch
  //     per block.
  maxBlocksInCache: 30,
  blockLoadDebounceMillis: 100,
};

// Additional props for grids with editable cells.
export const editableGridProps = {
  stopEditingWhenCellsLoseFocus: true,
};

// `isRowMaster` for a grid whose every row opens a detail. One function
// object, so the grid is not handed a new option on every render.
export const everyRowIsMaster = () => true;

// Props for master-detail sub-grids. Row height comes from the theme (see the
// note on defaultGridProps for why it is not a grid option here). They span
// the master row with measured columns only (see "Column sizing" in the header).
export const detailGridDefaults = {
  ...scrollbarProps,
  ...merchantLayoutProps,
  autoSizeStrategy: AUTO_SIZE_TO_FILL,
  selectionColumnDef,
  cellSelection: true,
  popupParent: document.body,
  overlayComponentSelector,
};

// Props for compact dashboard widgets (ShortageTable, OutstandingPOsTable).
// Row height comes from the theme (see useAgGridTheme), so a widget's rows
// read like every full-page grid's.
//
// The header row is the first thing in the tile and has no fill, so the
// theme's taller header would show as empty space under the tile title.
// `headerHeight` shrinks it to the sort arrow's height (the theme's 18px
// icon); AG Grid centres the labels in whatever height it gets, so the column
// names start about where a chart tile's controls do. AG Grid sets the header
// row's height inline and offsets the rows by it, so CSS cannot shrink it.
// These are the only props that set a header height
// (utils/agGridTheme.rowheight.test.jsx).
//
// The widgets measure a column to its content alone: AG Grid's default 20px
// `autoSizePadding` would sit beside every label, and on a right-aligned
// number header all of it lands left of the sort arrow. With no slack, a sort
// has to re-size, because it shows the arrow on a header measured without it:
// the continuous strategy does, a client-side sort being a model update.
export const dashboardGridProps = {
  ...scrollbarProps,
  autoSizeStrategy: AUTO_SIZE_TO_CONTENTS,
  // The same header menu as every grid: Reset columns, in the merchant's
  // language, rather than AG Grid's English one.
  getColumnMenuItems,
  suppressMovableColumns: true,
  cellSelection: true,
  popupParent: document.body,
  // Render every column even when the tile is narrow enough to overflow before
  // sizing. Auto-sizing can only measure rendered cells, so a virtualized
  // (off-screen) action column would size to its header, not its button — the
  // half-width tiles are only a handful of columns, so this is cheap here.
  suppressColumnVirtualisation: true,
  // 1, not 0: AG Grid reads the measured width as a whole-pixel offsetWidth
  // (`getPreferredWidthForElements`), which can round a label down below its
  // text and cut it off with an ellipsis.
  autoSizePadding: 1,
  headerHeight: 20,
  overlayComponentSelector,
};

// The Home tiles' columns: fixed in place and in width, unlike every other
// grid's (defaultColDef).
export const dashboardColDef = {
  ...defaultColDef,
  resizable: false,
};

// Right-click menu with copy only — NO export. The single export path is the
// toolbar <GridExportButton> (backend-driven). A grid that sets no
// getContextMenuItems falls back to AG Grid Enterprise's default menu, which
// re-introduces native CSV/Excel Export; use this on unified grids (and their
// master/detail sub-grids) to keep copy while suppressing that native export.
export const copyOnlyContextMenuItems = () => ['copy', 'copyWithHeaders'];

// A server-sorted grid shows the server's own order as the `initialSort` of the
// column it orders by, so the header carries the arrow for the order the rows
// come in. This puts that sort back and clears every other, for a page control
// that sorted the grid and is switched off again.
export function restoreInitialSort(api) {
  const state = (api.getColumns() ?? [])
    .filter((column) => column.getColDef().initialSort)
    .map((column) => ({ colId: column.getColId(), sort: column.getColDef().initialSort }));
  api.applyColumnState({ state, defaultState: { sort: null } });
}

// Shared colDef flags for any cell that holds an interactive control rather than
// data: not sortable/filterable/editable, locked to the grid's end (which
// also makes it immovable), hidden from the column picker, centered. Used by
// both action-column factories below.
const ACTION_COL_BASE = {
  sortable: false,
  filter: false,
  editable: false,
  resizable: false,
  lockPosition: 'right',
  suppressColumnsToolPanel: true,
  suppressNavigable: true,
  headerClass: 'ag-center-aligned-header',
  cellClass: 'ag-action-cell',
};

// Floor for auto-sized action columns. Auto-sizing only measures the
// currently-rendered rows, so a column whose renderer returns null for some rows
// (e.g. Shortage's "no supplier" rows) would collapse to its header width if no
// button-bearing row is in view when sizing runs — and a button revealed on scroll
// would then clip. This floor is a lower bound, NOT a computed exact width: a
// longer label still autosizes ABOVE it.
const ACTION_COL_MIN_WIDTH = 90;

// Text-button action column (Create PO, Receive, Link to Exact, …).
//
// Never carries a fixed width: the column stays auto-sizeable, so AG Grid
// measures the REAL rendered button DOM (padding, border, locale-translated
// label included) and grows it to fit its widest button.
//
// The row's main action — its next step (Review & create, Receive, Fix, Link
// to Exact) — is `variant="contained"`, so one action reads the same on every
// grid. A button beside it (Download), plain navigation (Open), a pair of
// equal choices (Keep / Retire) and a delete (`color="error"`) are
// `variant="outlined"`.
export const actionColumn = (overrides = {}) => ({
  ...ACTION_COL_BASE,
  minWidth: ACTION_COL_MIN_WIDTH,
  ...overrides,
});

// Icon-button action column (⋯ overflow menu, edit / delete / graph icon).
// Fixed narrow width — the icon is a fixed size, so there is nothing to measure.
// Always opts out of autosize (an empty header + lone icon would otherwise let
// auto-sizing collapse it). Defaults to CONTROL_COLUMN_WIDTH, the same
// as the checkbox column; pass `width` only for a multi-icon cluster (e.g.
// Apply/Edit/Delete).
export const iconActionColumn = ({ width = CONTROL_COLUMN_WIDTH, ...rest } = {}) => ({
  ...ACTION_COL_BASE,
  headerName: '',
  width,
  minWidth: width,
  maxWidth: width,
  suppressAutoSize: true,
  ...rest,
});
